import { Alert, Box, Skeleton, Stack, Typography } from "@mui/material";
import type { AffiliateCodeError } from "Components/Affiliate/AffiliateCodeField/affiliateCodeErrors";
import { InputActionButton } from "Components/Shared/Inputs/InputActionButton";
import { TextInput } from "Components/Shared/Inputs/TextInput";
import type { AffiliateReferrerDto } from "Infrastructure/Api/Api";
import type { KeyboardEvent } from "react";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Affiliate.CodeField;
// The field's full height (label, input and helper line), so it does not jump once loaded.
const FIELD_HEIGHT = 89;

export type AffiliateCodePublisher = Pick<
  AffiliateReferrerDto,
  "code" | "publisherDisplayName"
>;

type Props = {
  /** The affiliate already active for the role — the field shows it and cannot be changed. */
  lockedReferrer?: AffiliateCodePublisher | null;
  /** The active affiliate is still being looked up — nothing to show or type yet. */
  isLoading?: boolean;
  inputValue: string;
  onInputChange: (value: string) => void;
  onVerify: () => void;
  /** Drops a verified code. Without it the verified code stays (e.g. already applied). */
  onRemove?: () => void;
  verification: AffiliateCodePublisher | null;
  error: AffiliateCodeError | null;
  isVerifying: boolean;
};

/**
 * The registration "Affiliate kód" field (ZS-1024): locked on an active affiliate, otherwise an
 * optional code with an "Ověřit" button that names who the code belongs to.
 */
export const AffiliateCodeField = ({
  lockedReferrer,
  isLoading,
  inputValue,
  onInputChange,
  onVerify,
  onRemove,
  verification,
  error,
  isVerifying,
}: Props) => {
  const { t } = useResource();

  const getPublisherText = ({
    publisherDisplayName,
  }: AffiliateCodePublisher) =>
    publisherDisplayName
      ? t(RESOURCES.RecommendedBy, { name: publisherDisplayName })
      : t(RESOURCES.Verified);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onVerify();
    }
  };

  if (isLoading) {
    return <Skeleton variant="rounded" height={FIELD_HEIGHT} />;
  }

  if (lockedReferrer) {
    const { code, publisherDisplayName } = lockedReferrer;

    return (
      <TextInput
        name="affiliateCode"
        label={t(RESOURCES.Label)}
        value={code}
        disabled
        helperText={
          publisherDisplayName
            ? t(RESOURCES.LockedBy, { name: publisherDisplayName })
            : t(RESOURCES.Locked)
        }
      />
    );
  }

  if (verification) {
    return (
      <Alert severity="success" onClose={onRemove}>
        <Typography fontWeight={600}>{verification.code}</Typography>
        <Typography variant="body2">
          {getPublisherText(verification)}
        </Typography>
      </Alert>
    );
  }

  return (
    <Stack direction="row" gap={1} alignItems="center">
      <Box flex={1}>
        <TextInput
          name="affiliateCode"
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          label={t(RESOURCES.Label)}
          placeholder={t(RESOURCES.Placeholder)}
          error={!!error}
          helperText={error ? t(RESOURCES.Error[error]) : " "}
          disabled={isVerifying}
          onKeyDown={handleKeyDown}
        />
      </Box>
      <InputActionButton
        onClick={onVerify}
        isDisabled={isVerifying || !inputValue.trim()}
        isPending={isVerifying}
      >
        {t(Resources.Common.Verify)}
      </InputActionButton>
    </Stack>
  );
};
